import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { Suburb, PriceInfo, RepaymentInfo } from '~/common/models';
import { getVehiclePrice } from '~/common/services/pricing-service';
import {
  getRepayments,
  getFinanceFormInput,
} from '~/common/services/finance-service';

interface VehicleRequest {
  id: string;
  pimIdentifier: string;
  additionalOptionsPrice?: string;
}

interface PriceState {
  priceInfo: PriceInfo | null;
  repaymentInfo: RepaymentInfo | null;
  isLoading: boolean;
  isError: boolean;
}

interface PricingQueueContextType {
  registerVehicle: (vehicle: VehicleRequest) => void;
  getPricingState: (id: string) => PriceState;
  globalSuburb: Suburb | null;
}

const PricingQueueContext = createContext<PricingQueueContextType | undefined>(
  undefined
);

export const PricingQueueProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [globalSuburb, setGlobalSuburb] = useState<Suburb | null>(null);
  const [pricingMap, setPricingMap] = useState<Record<string, PriceState>>({});

  const registeredVehicles = useRef<Map<string, VehicleRequest>>(new Map());
  const requestQueue = useRef<VehicleRequest[]>([]);
  const isProcessing = useRef(false);

  const registerVehicle = useCallback((vehicle: VehicleRequest) => {
    if (!registeredVehicles.current.has(vehicle.id)) {
      registeredVehicles.current.set(vehicle.id, vehicle);
      setPricingMap((prev) => ({
        ...prev,
        [vehicle.id]: {
          priceInfo: null,
          repaymentInfo: null,
          isLoading: false,
          isError: false,
        },
      }));
    }
  }, []);

  const getPricingState = useCallback(
    (id: string) => {
      return (
        pricingMap[id] || {
          priceInfo: null,
          repaymentInfo: null,
          isLoading: false,
          isError: false,
        }
      );
    },
    [pricingMap]
  );

  const processQueue = useCallback(
    async (suburb: Suburb) => {
      if (isProcessing.current || requestQueue.current.length === 0) return;

      isProcessing.current = true;

      while (requestQueue.current.length > 0) {
        if (
          globalSuburb?.postcode !== suburb.postcode &&
          globalSuburb !== null
        ) {
          break;
        }

        const batch = requestQueue.current.splice(0, 2);

        await Promise.all(
          batch.map(async (vehicle) => {
            try {
              const price = await getVehiclePrice(
                vehicle.id,
                suburb.postcode,
                vehicle.additionalOptionsPrice
              );

              const repayment = await getRepayments(
                vehicle.pimIdentifier,
                getFinanceFormInput(null),
                suburb.postcode
              );

              setPricingMap((prev) => ({
                ...prev,
                [vehicle.id]: {
                  priceInfo: price,
                  repaymentInfo: repayment,
                  isLoading: false,
                  isError: false,
                },
              }));
            } catch (err) {
              console.error(`Failed to load pricing for ${vehicle.id}`, err);
              setPricingMap((prev) => ({
                ...prev,
                [vehicle.id]: {
                  ...prev[vehicle.id],
                  isLoading: false,
                  isError: true,
                },
              }));
            }
          })
        );

        // Yield to main thread
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      isProcessing.current = false;
    },
    [globalSuburb]
  );

  // Listen for location changes globally
  useEffect(() => {
    const handleLocationSelected = (e: CustomEvent<{ suburb?: Suburb }>) => {
      const suburb = e.detail?.suburb;
      if (suburb) {
        setGlobalSuburb(suburb);

        // Reset state & fill queue
        const newMap: Record<string, PriceState> = {};
        const newQueue: VehicleRequest[] = [];

        registeredVehicles.current.forEach((vehicle, id) => {
          newMap[id] = {
            priceInfo: null,
            repaymentInfo: null,
            isLoading: true,
            isError: false,
          };
          newQueue.push(vehicle);
        });

        setPricingMap(newMap);
        requestQueue.current = newQueue;

        // Slight delay before processing to ensure UI renders loading states
        setTimeout(() => processQueue(suburb), 100);
      }
    };

    window.addEventListener('locationSelected' as any, handleLocationSelected);
    return () =>
      window.removeEventListener(
        'locationSelected' as any,
        handleLocationSelected
      );
  }, [processQueue]);

  return (
    <PricingQueueContext.Provider
      value={{ registerVehicle, getPricingState, globalSuburb }}
    >
      {children}
    </PricingQueueContext.Provider>
  );
};

export const usePricingQueue = () => {
  const context = useContext(PricingQueueContext);
  if (context === undefined) {
    throw new Error(
      'usePricingQueue must be used within a PricingQueueProvider'
    );
  }
  return context;
};
